import type { StudioRoute } from '@/store/types'
import {
  STUDIO_ROUTE_CANONICAL_PATH,
  STUDIO_ROUTE_MAP,
  STUDIO_SUB_ROUTE_CANONICAL_PATH,
} from './studio-route-registry'

/**
 * `/studio/members` → `members`, `/studio` → `''`.
 *
 * A single trailing slash is dropped so `/studio/`, `/studio/members/`, and
 * `/studio/members` all name the same screen. Without it only the exact
 * spelling resolved and every other one fell through to the unknown-route
 * path — `/studio/` included, which is unambiguously Home.
 */
export function studioSubRouteFromPathname(pathname: string): string {
  const subRoute = pathname.replace('/studio/', '').replace('/studio', '')
  return subRoute.endsWith('/') ? subRoute.slice(0, -1) : subRoute
}

/**
 * The Studio route a URL names, or `null` when the URL names nothing.
 *
 * Returning `null` is the whole point. This used to end in `?? 'home'`, so
 * `/studio/zzz-nonexistent` quietly rendered the home chat screen while the
 * address bar still read `/studio/zzz-nonexistent` — a typo or a stale link
 * looked exactly like a successful navigation, with nothing on screen to
 * contradict the wrong address. Callers must now decide what an unresolvable
 * URL means; none of them can fall into "home" by accident.
 */
export function resolveStudioRouteFromPathname(pathname: string): StudioRoute | null {
  return STUDIO_ROUTE_MAP[studioSubRouteFromPathname(pathname)] ?? null
}

export function isKnownStudioPathname(pathname: string): boolean {
  return resolveStudioRouteFromPathname(pathname) !== null
}

/**
 * The address this URL should be showing, or `undefined` when it is already
 * correct.
 *
 * Covers both kinds of drift between address and screen:
 *   - aliases (`/studio/audience` → `/studio/members`), and
 *   - non-canonical spellings of a registered route (`/studio/` → `/studio`),
 *     which previously kept their odd address forever because only aliases
 *     were normalised.
 *
 * Unregistered paths return `undefined` on purpose: they are not silently
 * rewritten to something plausible, they are reported as not found.
 */
export function canonicalStudioPathFromPathname(pathname: string): string | undefined {
  const subRoute = studioSubRouteFromPathname(pathname)
  const canonicalPath =
    STUDIO_ROUTE_CANONICAL_PATH[subRoute] ?? STUDIO_SUB_ROUTE_CANONICAL_PATH[subRoute]
  if (!canonicalPath || canonicalPath === pathname) return undefined
  return canonicalPath
}
